/**
 * ADR-200 amendment (2026-09-29) — scoping is the operator's per-run choice, off by default.
 *
 * The owner's decision: "Make scoping a run-time decision by the operator using the
 * assistant. Just a check box for each run, with off by default." Scoping drafts a brief,
 * asks clarifying questions and waits for a person to confirm it; measured on 9 production
 * requests it roughly doubled wall time and tripled cost, so it is opt-in per run.
 *
 * The box is offered ONLY when the send would open a new authoring run: the server reads
 * `scoping` from the request that opens a run and ignores it on every later one, so a box
 * shown on a run already in progress would be a control that does nothing. Once a run
 * exists, a run that scopes says so read-only; a run that does not shows nothing.
 */
import { useEffect, useState } from 'react'
import { api, type CopilotIntent } from '../api/client'

/** What this slot holds, as far as the composer needs to know. `unknown` (not read yet,
 *  unreadable, or no session store) never offers the box: a send then opens an ordinary,
 *  unscoped run, which is the default anyway. */
export type SlotRun =
  | { state: 'unknown' }
  | { state: 'none' }
  | { state: 'run'; scoping: boolean }

export type ScopingControl = 'offer' | 'locked-on' | 'hidden'

/** Which form the scoping control takes for the next send. */
export function scopingControlFor(intent: CopilotIntent, slot: SlotRun, sending: boolean): ScopingControl {
  if (slot.state === 'run') return slot.scoping ? 'locked-on' : 'hidden'
  // An `ask` turn opens no run, and a send in flight is about to open one.
  if (slot.state === 'none' && intent === 'edit' && !sending) return 'offer'
  return 'hidden'
}

/** The `scoping` value a send carries: the box, only when it was offered. */
export function scopingForSend(control: ScopingControl, checked: boolean): boolean {
  return control === 'offer' && checked
}

/**
 * Whether the operator's slot already holds a run. Re-read whenever `refreshKey` changes (the
 * page passes its in-flight flag, so the answer is fresh after every send) and every 10s, for
 * a run discarded or started from another tab. A value read for another slot is never
 * returned for this one.
 */
export function useSlotRun(agent: string, workflow: string, refreshKey: unknown): SlotRun {
  const key = `${agent}\u0000${workflow}`
  const [read, setRead] = useState<{ key: string; slot: SlotRun }>({ key: '', slot: { state: 'unknown' } })
  useEffect(() => {
    if (!agent || !workflow) return
    let live = true
    const load = async () => {
      try {
        const r = await api.authoringRun(agent, workflow)
        if (!live) return
        const slot: SlotRun = r.not_configured
          ? { state: 'unknown' }
          : r.run ? { state: 'run', scoping: !!r.run.scoping_enabled } : { state: 'none' }
        setRead({ key, slot })
      } catch {
        if (live) setRead({ key, slot: { state: 'unknown' } })
      }
    }
    void load()
    const t = setInterval(() => { if (!document.hidden) void load() }, 10000)
    return () => { live = false; clearInterval(t) }
  }, [agent, workflow, key, refreshKey])
  return read.key === key ? read.slot : { state: 'unknown' }
}

export function ScopeFirstControl({
  control,
  checked,
  onChange,
  disabled,
}: {
  control: ScopingControl
  checked: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
}) {
  if (control === 'hidden') return null
  const locked = control === 'locked-on'
  return (
    <label
      className="flex items-center gap-1.5 px-2 pt-1.5 text-[11px] text-gray-700"
      data-testid="scope-first"
    >
      <input
        type="checkbox"
        checked={locked ? true : checked}
        disabled={locked || disabled}
        onChange={e => onChange(e.target.checked)}
        aria-describedby="scope-first-hint"
        className="h-3.5 w-3.5"
      />
      <span className="font-medium">Scope this request first</span>
      <span id="scope-first-hint" className="text-gray-400">
        {locked
          ? '— this run was started with scoping, which applies to the whole run.'
          : '— asks clarifying questions and has you confirm a brief before building. Slower.'}
      </span>
    </label>
  )
}
